/* =========================================================
   CANNIBISTERS — Shop category dropdown controller
   Added 2026-08-21, rewritten 2026-08-21 (v2) to use FULLY DELEGATED
   event handling on `document` instead of listeners bound to specific
   nodes. Rationale: a real-mouse repro showed the panel intermittently
   failed to open even though the wrapper was still bound
   (`__cbShopDropdownBound === true`) and still in the document — the
   safest explanation given a sticky/animated header that reflows and
   re-dispatches section markup is that some intermediate re-render
   passed a *stale* handle to addEventListener before the browser had
   settled the final layout, or a listener silently detached in a way
   node-level bookkeeping didn't catch. Delegation removes that whole
   class of failure: there is nothing to rebind, ever, because the
   listeners live on `document` and re-query the DOM on every event.

   Handles two levels of the same interaction pattern:
   - the main dropdown:  li[data-shop-dropdown] > a[data-shop-dropdown-trigger] + #ShopDropdownPanel
   - a flyout (Flower/Joints): li[data-shop-flyout] > a[data-shop-flyout-trigger] + .shop-dropdown__flyout

   - Desktop hover: 120ms open delay, 250ms close delay (forgiving
     bridge — mouseover/mouseout are used instead of mouseenter/
     mouseleave because they bubble, so a single delegated listener on
     `document` can emulate enter/leave via relatedTarget containment
     checks; moving from a trigger into its own panel/flyout never
     leaves the container's DOM subtree, so the bridge holds for free).
   - Keyboard: Enter/Space toggles, ArrowDown enters the panel/flyout,
     ArrowRight opens a flyout, ArrowLeft/Escape backs out one level
     and refocuses the parent trigger, Escape at the top level closes
     everything and refocuses Shop.
   - Touch / no-hover pointers: first tap opens instead of navigating;
     second tap on an already-open trigger navigates normally.
   - Closes on outside click and when focus truly leaves a container.
   - Flyouts flip to the left edge if they'd overflow the viewport.
   ========================================================= */
(function () {
  if (window.__cbShopDropdownLoaded) return;
  window.__cbShopDropdownLoaded = true;

  var OPEN_DELAY = 120;
  var CLOSE_DELAY = 250;
  var CONTAINER_SELECTOR = '[data-shop-dropdown], [data-shop-flyout]';
  var TRIGGER_SELECTOR = '[data-shop-dropdown-trigger], [data-shop-flyout-trigger]';

  var timerStore = typeof WeakMap === 'function' ? new WeakMap() : null;
  var fallbackTimers = []; // {el, openTimer, closeTimer} used only if WeakMap unsupported

  function canHover() {
    return !!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches);
  }

  function getTimers(el) {
    if (timerStore) {
      var t = timerStore.get(el);
      if (!t) { t = { openTimer: null, closeTimer: null }; timerStore.set(el, t); }
      return t;
    }
    for (var i = 0; i < fallbackTimers.length; i++) {
      if (fallbackTimers[i].el === el) return fallbackTimers[i];
    }
    var rec = { el: el, openTimer: null, closeTimer: null };
    fallbackTimers.push(rec);
    return rec;
  }

  function clearTimers(el) {
    var t = getTimers(el);
    if (t.openTimer) { clearTimeout(t.openTimer); t.openTimer = null; }
    if (t.closeTimer) { clearTimeout(t.closeTimer); t.closeTimer = null; }
  }

  function getTrigger(container) {
    return container.querySelector(TRIGGER_SELECTOR);
  }

  function isFlyout(container) {
    return container.hasAttribute('data-shop-flyout');
  }

  function positionFlyout(container) {
    if (!isFlyout(container)) return;
    var flyout = container.querySelector('.shop-dropdown__flyout');
    if (!flyout) return;
    flyout.classList.remove('shop-dropdown__flyout--flip');
    var rect = flyout.getBoundingClientRect();
    if (rect.right > window.innerWidth - 8) {
      flyout.classList.add('shop-dropdown__flyout--flip');
    }
  }

  function openContainer(container) {
    clearTimers(container);
    container.classList.add('is-open');
    var trigger = getTrigger(container);
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    positionFlyout(container);
  }

  function closeContainer(container) {
    clearTimers(container);
    container.classList.remove('is-open');
    var trigger = getTrigger(container);
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    // collapse any nested flyout immediately (no lingering open state)
    var nested = container.querySelectorAll('[data-shop-flyout]');
    for (var i = 0; i < nested.length; i++) {
      clearTimers(nested[i]);
      nested[i].classList.remove('is-open');
      var nt = getTrigger(nested[i]);
      if (nt) nt.setAttribute('aria-expanded', 'false');
    }
  }

  function toggleContainer(container) {
    if (container.classList.contains('is-open')) closeContainer(container);
    else openContainer(container);
  }

  function scheduleOpen(container) {
    clearTimers(container);
    var t = getTimers(container);
    t.openTimer = setTimeout(function () { openContainer(container); }, OPEN_DELAY);
  }

  function scheduleClose(container) {
    clearTimers(container);
    var t = getTimers(container);
    t.closeTimer = setTimeout(function () { closeContainer(container); }, CLOSE_DELAY);
  }

  function focusFirstLink(container) {
    var scope = isFlyout(container)
      ? container.querySelector('.shop-dropdown__flyout')
      : document.getElementById('ShopDropdownPanel');
    if (!scope) return;
    var link = scope.querySelector('a');
    if (link) link.focus();
  }

  function closeEverythingAndRefocus() {
    var main = document.querySelector('[data-shop-dropdown]');
    if (!main) return;
    closeContainer(main);
    var trigger = getTrigger(main);
    if (trigger) trigger.focus();
  }

  /* ---- delegated hover (mouseover/mouseout emulate enter/leave) ---- */
  document.addEventListener('mouseover', function (e) {
    if (!canHover()) return;
    var container = e.target.closest(CONTAINER_SELECTOR);
    if (!container) return;
    var related = e.relatedTarget;
    if (related && container.contains(related)) return; // moved within, not a real enter
    scheduleOpen(container);
  });

  document.addEventListener('mouseout', function (e) {
    if (!canHover()) return;
    var container = e.target.closest(CONTAINER_SELECTOR);
    if (!container) return;
    var related = e.relatedTarget;
    if (related && container.contains(related)) return; // still within
    scheduleClose(container);
  });

  /* ---- delegated click: touch first-tap-opens + outside click close ---- */
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest(TRIGGER_SELECTOR);
    if (trigger) {
      var container = trigger.closest(CONTAINER_SELECTOR);
      if (!container) return;
      if (!canHover()) {
        if (!container.classList.contains('is-open')) {
          e.preventDefault();
          clearTimers(container);
          openContainer(container);
        }
        // already open on a no-hover device: let the tap navigate
      }
      return;
    }
    // outside click closes any open dropdown/flyout the click landed outside of
    var openContainers = document.querySelectorAll('[data-shop-dropdown].is-open, [data-shop-flyout].is-open');
    for (var i = 0; i < openContainers.length; i++) {
      if (!openContainers[i].contains(e.target)) closeContainer(openContainers[i]);
    }
  });

  /* ---- delegated keydown ---- */
  document.addEventListener('keydown', function (e) {
    var trigger = e.target.closest(TRIGGER_SELECTOR);
    if (trigger) {
      var container = trigger.closest(CONTAINER_SELECTOR);
      if (!container) return;
      var flyoutTrigger = trigger.hasAttribute('data-shop-flyout-trigger');

      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        toggleContainer(container);
        return;
      }
      if (e.key === 'ArrowDown' || e.key === 'Down') {
        e.preventDefault();
        openContainer(container);
        focusFirstLink(container);
        return;
      }
      if (flyoutTrigger && (e.key === 'ArrowRight' || e.key === 'Right')) {
        e.preventDefault();
        openContainer(container);
        focusFirstLink(container);
        return;
      }
      if (e.key === 'Escape' || e.key === 'Esc') {
        if (container.classList.contains('is-open')) {
          e.preventDefault();
          closeContainer(container);
          trigger.focus();
        } else if (isFlyout(container)) {
          // bubble up: close the whole thing from a closed flyout trigger too
          closeEverythingAndRefocus();
        }
        return;
      }
      return;
    }

    // focus is on a link inside the main panel or a flyout list
    var link = e.target.closest('#ShopDropdownPanel a, .shop-dropdown__flyout a');
    if (!link) return;

    var flyoutScope = link.closest('.shop-dropdown__flyout');
    var flyoutContainer = flyoutScope ? flyoutScope.closest('[data-shop-flyout]') : null;
    var mainWrapper = document.querySelector('[data-shop-dropdown]');

    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      if (flyoutContainer) {
        closeContainer(flyoutContainer);
        var ft = getTrigger(flyoutContainer);
        if (ft) ft.focus();
      } else {
        closeEverythingAndRefocus();
      }
      return;
    }

    if ((e.key === 'ArrowLeft' || e.key === 'Left') && flyoutContainer) {
      e.preventDefault();
      closeContainer(flyoutContainer);
      var ft2 = getTrigger(flyoutContainer);
      if (ft2) ft2.focus();
      return;
    }

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      var scope = flyoutScope || document.getElementById('ShopDropdownPanel');
      var links = Array.prototype.slice.call(scope.querySelectorAll('a'));
      var idx = links.indexOf(link);
      if (e.key === 'ArrowDown') {
        (links[idx + 1] || links[0]).focus();
      } else if (idx <= 0) {
        var backTrigger = flyoutContainer ? getTrigger(flyoutContainer) : (mainWrapper && getTrigger(mainWrapper));
        if (backTrigger) backTrigger.focus();
      } else {
        links[idx - 1].focus();
      }
    }
  });

  /* ---- delegated focusout: close a container once focus truly leaves it ---- */
  document.addEventListener('focusout', function (e) {
    var container = e.target.closest(CONTAINER_SELECTOR);
    if (!container) return;
    // relatedTarget is unreliable across browsers on blur timing; re-check
    // document.activeElement on the next tick instead.
    setTimeout(function () {
      if (!container.contains(document.activeElement)) {
        clearTimers(container);
        closeContainer(container);
      }
    }, 0);
  }, true);

  window.addEventListener('resize', function () {
    var openFlyouts = document.querySelectorAll('[data-shop-flyout].is-open');
    for (var i = 0; i < openFlyouts.length; i++) positionFlyout(openFlyouts[i]);
  });
})();
